import { type Dispatch, useState } from "react";

import { AnalyticsDataSource, type CurrencyCode, type DataHubMetrics } from "@doitintl/cmp-models";

import { useCloudAnalyticsMetadata } from "../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { type MetricWSnap, type ReportWSnap } from "../../types";
import { type AttributionGroupWithRef } from "./attributionGroups/types";
import {
  CreateTemplateContextProvider,
  MetricSplitsContextProvider,
  ReportConfigContextProvider,
  ReportContextProvider,
  ReportDimensionsContextProvider,
  ReportExportContextProvider,
  ReportSaverContextProvider,
  ReportTemplateContextProvider,
} from "./Context";
import { type ReportTemplatesAction } from "./reducers/ReportTemplatesReducer";
import Report from "./Report";
import { type ReportTemplateCard } from "./templateLibrary/types";

type Props = {
  report: ReportWSnap;
  calculatedMetrics: MetricWSnap[];
  filteredExtendedMetrics;
  defaultCurrency?: CurrencyCode | null;
  excludeSelectMetadataIds: Set<string>;
  handleRevertReport: () => void;
  attributionGroups: AttributionGroupWithRef[];
  reportTemplateId: string;
  isFavoriteReportTemplate: boolean;
  reportTemplateCards: ReportTemplateCard[];
  dispatchReportTemplates: Dispatch<ReportTemplatesAction>;
  datahubMetrics: DataHubMetrics[];
};

const ReportContextWrapper = ({
  report,
  calculatedMetrics,
  filteredExtendedMetrics,
  defaultCurrency,
  excludeSelectMetadataIds,
  handleRevertReport,
  attributionGroups,
  reportTemplateId,
  isFavoriteReportTemplate,
  reportTemplateCards,
  dispatchReportTemplates,
  datahubMetrics,
}: Props) => {
  const { hasDataHub } = useCloudAnalyticsMetadata();

  let dataSource = report?.data?.config?.dataSource;

  const [rawReportData, setRawReportData] = useState<unknown[][]>([[]]);

  if (!dataSource) {
    dataSource = hasDataHub ? AnalyticsDataSource.BILLING_DATAHUB : AnalyticsDataSource.BILLING;
  } else if (dataSource === AnalyticsDataSource.BILLING_DATAHUB && !hasDataHub) {
    dataSource = AnalyticsDataSource.BILLING;
  }

  return (
    <ReportContextProvider report={report} rawReportData={rawReportData}>
      <ReportConfigContextProvider
        props={{
          calcMetrics: calculatedMetrics,
          extendedMetrics: filteredExtendedMetrics,
          datahubMetrics,
          currency: defaultCurrency,
          excludeSelectMetadataIds,
          dataSource,
        }}
      >
        <ReportSaverContextProvider>
          <ReportDimensionsContextProvider>
            <MetricSplitsContextProvider>
              <ReportTemplateContextProvider
                reportTemplateId={reportTemplateId}
                isFavoriteReportTemplate={isFavoriteReportTemplate}
                reportTemplateCards={reportTemplateCards}
                dispatchReportTemplates={dispatchReportTemplates}
              >
                <CreateTemplateContextProvider>
                  <ReportExportContextProvider>
                    <Report
                      handleRevertReport={handleRevertReport}
                      attributionGroups={attributionGroups}
                      setRawReportData={setRawReportData}
                    />
                  </ReportExportContextProvider>
                </CreateTemplateContextProvider>
              </ReportTemplateContextProvider>
            </MetricSplitsContextProvider>
          </ReportDimensionsContextProvider>
        </ReportSaverContextProvider>
      </ReportConfigContextProvider>
    </ReportContextProvider>
  );
};

export default ReportContextWrapper;
